import { useEffect, useRef, useState } from 'react';
import { Form, ListGroup, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { suggestItemName } from '../utils/hsnSuggestions';

/**
 * Type-ahead "search HSN code" input, backed by the curated hsn_codes reference table (see
 * V138__Create_hsn_codes.sql) instead of a blind free-text field. Matches on code prefix or
 * description substring; picking a result sets the code text and shows its description as a hint.
 * Never blocks typing/submission - an unmatched or custom code is still accepted as free text,
 * since the curated list won't cover every case yet.
 */
function HsnLookupInput({ value, onChange, onSelect, nameValue, onUseName, name = 'hsCode', disabled }) {
  const [results, setResults] = useState([]);
  const [matchedDescription, setMatchedDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [pickedResult, setPickedResult] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const q = (value || '').trim();
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const response = await axios.get('/api/hsn-codes/search', { params: { q } });
        setResults(response.data || []);
      } catch (error) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [value]);

  useEffect(() => {
    const exact = results.find((r) => r.code === (value || '').trim());
    setMatchedDescription(exact ? exact.description : '');
  }, [results, value]);

  const handleSelect = (result) => {
    onChange({ target: { name, value: result.code } });
    setMatchedDescription(result.description);
    setPickedResult(result);
    setShowResults(false);
    if (onSelect) onSelect(result);
  };

  return (
    <div className="position-relative">
      <div className="d-flex align-items-center gap-2">
        <Form.Control
          type="text"
          name={name}
          value={value}
          onChange={(e) => {
            onChange(e);
            setShowResults(true);
          }}
          onFocus={() => setShowResults(true)}
          onBlur={() => setTimeout(() => setShowResults(false), 150)}
          placeholder="Search HSN code or description"
          disabled={disabled}
          autoComplete="off"
        />
        {loading && <Spinner animation="border" size="sm" />}
      </div>
      {showResults && results.length > 0 && (
        <ListGroup
          className="position-absolute w-100 shadow-sm"
          style={{ zIndex: 1000, maxHeight: 220, overflowY: 'auto' }}
        >
          {results.map((r) => (
            <ListGroup.Item
              key={r.id}
              action
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(r)}
            >
              <div title={r.description}>
                <strong>{r.code}</strong>
                <div
                  className="small"
                  style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                >
                  {r.description}
                </div>
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
      {matchedDescription && (
        <Form.Text className="text-success">
          {matchedDescription.length > 220 ? `${matchedDescription.slice(0, 220)}…` : matchedDescription}
        </Form.Text>
      )}
      {onUseName && pickedResult && pickedResult.code === (value || '').trim() && !(nameValue || '').trim() && (
        <div className="small mt-1">
          Suggested name: <strong>{suggestItemName(pickedResult.description)}</strong>{' '}
          <button
            type="button"
            className="btn btn-link btn-sm p-0 align-baseline"
            onClick={() => onUseName(suggestItemName(pickedResult.description))}
          >
            Use as name
          </button>
        </div>
      )}
      {!matchedDescription && (value || '').trim().length > 0 && !loading && results.length === 0 && (
        <Form.Text className="text-muted">
          No match in the reference list - kept as entered, you can still save it
        </Form.Text>
      )}
    </div>
  );
}

export default HsnLookupInput;
